/**
 * Customers Page - Claude.ai-inspired Design
 * List and manage customers with search and card grid layout
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Search,
  Mail,
  Phone,
  MapPin,
  Calendar,
  FileText,
  Plus,
  TrendingUp,
  Archive,
  RotateCcw,
  Tag,
  Eye,
  EyeOff,
} from 'lucide-react';
import apiClient, { type ApiResponse } from '../lib/api-client';
import { CustomersListSkeleton } from '../components/ui/SkeletonLoaders';
import { useToast } from '../components/ui';

// Types
interface Customer {
  id: string;
  userId: string;
  email: string;
  name: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  dateOfBirth?: string;
  tags: string[];
  archived: boolean;
  createdAt: string;
  appointmentsCount: number;
  invoicesCount: number;
}

interface CustomersResponse {
  customers: Customer[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// Get initials from name
const getInitials = (name: string): string => {
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// Format date
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export default function CustomersPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'createdAt' | 'appointments'>('name');
  const [showArchived, setShowArchived] = useState(false);
  const [tagFilter, setTagFilter] = useState<string>('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['customers', searchTerm, sortBy, showArchived, tagFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      params.append('sortBy', sortBy);
      if (showArchived) {
        params.append('includeArchived', 'true');
      }
      if (tagFilter) {
        params.append('tag', tagFilter);
      }

      const response = await apiClient.get<ApiResponse<CustomersResponse>>(
        `/customers?${params.toString()}`
      );
      return response.data.data;
    },
  });

  const archiveMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post<ApiResponse<Customer>>(`/customers/${id}/archive`);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      showToast('success', 'Customer archived');
    },
    onError: () => {
      showToast('error', 'Failed to archive customer');
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.post<ApiResponse<Customer>>(`/customers/${id}/restore`);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      showToast('success', 'Customer restored');
    },
    onError: () => {
      showToast('error', 'Failed to restore customer');
    },
  });

  if (isLoading) {
    return <CustomersListSkeleton />;
  }

  if (error) {
    return (
      <div className="claude-card border-destructive/50 bg-destructive/5 p-6">
        <div className="flex items-center gap-3 text-destructive">
          <Users className="w-5 h-5 flex-shrink-0" />
          <div>
            <p className="font-semibold">Failed to load customers</p>
            <p className="text-sm mt-1 opacity-90">Please try refreshing the page</p>
          </div>
        </div>
      </div>
    );
  }

  const customers = data?.customers || [];
  const allTags = [...new Set(customers.flatMap((c: Customer) => c.tags || []))].sort();
  const stats = {
    total: customers.length,
    totalAppointments: customers.reduce((sum: number, c: Customer) => sum + c.appointmentsCount, 0),
    totalInvoices: customers.reduce((sum: number, c: Customer) => sum + c.invoicesCount, 0),
    avgAppointments:
      customers.length > 0
        ? Math.round(
            customers.reduce((sum: number, c: Customer) => sum + c.appointmentsCount, 0) /
              customers.length
          )
        : 0,
  };

  return (
    <div className="space-y-8 pb-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <h1>Customers</h1>
          <p className="text-muted-foreground text-lg">Manage your customer relationships</p>
        </div>
        <button className="claude-button claude-button-primary">
          <Plus className="w-4 h-4" />
          Add Customer
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        <div className="claude-card p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Customers</p>
              <p className="text-2xl font-semibold tracking-tight">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="claude-card p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Appointments</p>
              <p className="text-2xl font-semibold tracking-tight">{stats.totalAppointments}</p>
            </div>
          </div>
        </div>

        <div className="claude-card p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-success" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Invoices</p>
              <p className="text-2xl font-semibold tracking-tight">{stats.totalInvoices}</p>
            </div>
          </div>
        </div>

        <div className="claude-card p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-warning" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Avg Appointments</p>
              <p className="text-2xl font-semibold tracking-tight">{stats.avgAppointments}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search, Sort, and Filters */}
      <div className="claude-card p-6">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Search */}
          <div className="flex-1 min-w-[240px] relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
            <input
              type="text"
              placeholder="Fuzzy search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="claude-input pl-10 w-full"
            />
          </div>

          {/* Sort */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Sort by:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="claude-input w-auto"
            >
              <option value="name">Name</option>
              <option value="createdAt">Date Added</option>
              <option value="appointments">Appointments</option>
            </select>
          </div>

          {/* Tag Filter */}
          {allTags.length > 0 && (
            <div className="flex items-center gap-2">
              <Tag className="w-4 h-4 text-muted-foreground" />
              <select
                value={tagFilter}
                onChange={(e) => setTagFilter(e.target.value)}
                className="claude-input w-auto"
              >
                <option value="">All Tags</option>
                {allTags.map((tag) => (
                  <option key={tag} value={tag}>
                    {tag}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Show Archived Toggle */}
          <button
            onClick={() => setShowArchived(!showArchived)}
            className={`claude-button text-sm ${
              showArchived ? 'claude-button-primary' : 'claude-button-secondary'
            }`}
          >
            {showArchived ? (
              <>
                <Eye className="w-4 h-4" /> Showing Archived
              </>
            ) : (
              <>
                <EyeOff className="w-4 h-4" /> Hide Archived
              </>
            )}
          </button>
        </div>
      </div>

      {/* Customers Grid */}
      {customers.length === 0 ? (
        <div className="claude-card">
          <div className="claude-empty-state">
            <Users className="claude-empty-state-icon" />
            <h3 className="claude-empty-state-title">No customers found</h3>
            <p className="claude-empty-state-description">
              {searchTerm
                ? 'Try adjusting your search term'
                : 'Add your first customer to get started'}
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {customers.map((customer) => (
            <div
              key={customer.id}
              className={`claude-card p-6 hover:shadow-md transition-shadow cursor-pointer space-y-4 ${
                customer.archived ? 'opacity-70' : ''
              }`}
            >
              {/* Header with Avatar */}
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-primary font-semibold text-lg">
                    {getInitials(customer.name)}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-lg truncate">{customer.name}</h3>
                    {customer.archived && (
                      <span className="claude-badge claude-badge-warning text-[10px]">
                        Archived
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground truncate">
                    Member since {formatDate(customer.createdAt)}
                  </p>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span className="truncate">{customer.email}</span>
                </div>
                {customer.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span>{customer.phone}</span>
                  </div>
                )}
                {(customer.city || customer.state) && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <span className="truncate">
                      {[customer.city, customer.state].filter(Boolean).join(', ')}
                    </span>
                  </div>
                )}
              </div>

              {/* Tags */}
              {(customer.tags || []).length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {customer.tags.slice(0, 3).map((tag) => (
                    <span key={tag} className="claude-badge claude-badge-default text-[10px]">
                      {tag}
                    </span>
                  ))}
                  {customer.tags.length > 3 && (
                    <span className="claude-badge claude-badge-default text-[10px]">
                      +{customer.tags.length - 3}
                    </span>
                  )}
                </div>
              )}

              {/* Stats */}
              <div className="pt-4 border-t border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{customer.appointmentsCount}</span>
                  <span className="text-sm text-muted-foreground">appointments</span>
                </div>
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{customer.invoicesCount}</span>
                  <span className="text-sm text-muted-foreground">invoices</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => navigate(`/customers/${customer.id}`)}
                  className="claude-button claude-button-secondary flex-1 text-sm"
                >
                  View Details
                </button>
                <button className="claude-button claude-button-ghost text-sm">
                  <Mail className="w-4 h-4" />
                </button>
                {customer.archived ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      restoreMutation.mutate(customer.id);
                    }}
                    disabled={restoreMutation.isPending}
                    className="claude-button claude-button-ghost text-sm"
                    title="Restore customer"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      archiveMutation.mutate(customer.id);
                    }}
                    disabled={archiveMutation.isPending}
                    className="claude-button claude-button-ghost text-sm"
                    title="Archive customer"
                  >
                    <Archive className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {data?.pagination && data.pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {customers.length} of {data.pagination.total} customers
          </p>
          <div className="flex gap-2">
            <button
              disabled={data.pagination.page === 1}
              className="claude-button claude-button-secondary"
            >
              Previous
            </button>
            <button
              disabled={data.pagination.page === data.pagination.pages}
              className="claude-button claude-button-secondary"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
